import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Settings, ExternalLink, CheckCircle, Clock, X, Unlink, Copy, Crown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserPlan } from '../hooks/useUserPlan';
import { Clip } from '../types';
import ConfirmationModal from './ConfirmationModal';

interface SocialMediaUploaderProps {
  clip: Clip;
  className?: string;
}

interface ConnectedPlatform {
  connected: boolean;
  channel_title?: string;
  channel_thumbnail?: string;
  username?: string;
  display_name?: string;
  avatar_url?: string;
  connected_at?: string;
}

interface ConnectedPlatforms {
  youtube?: ConnectedPlatform;
  tiktok?: ConnectedPlatform;
}

interface UploadHistory {
  id: string;
  platform: string;
  video_id: string;
  video_url: string;
  uploaded_at: string;
  metadata: {
    title?: string;
    description?: string;
    privacy?: string;
  };
}

const SocialMediaUploader: React.FC<SocialMediaUploaderProps> = ({ clip, className = '' }) => {
  const { currentUser, refreshSubscriptionStatus, isGuest } = useAuth();
  const { isPro, refetch: refetchPlanData } = useUserPlan();
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState<ConnectedPlatforms>({});
  const [uploadHistory, setUploadHistory] = useState<UploadHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, { step: string; progress: number }>>({});
  const [uploadForm, setUploadForm] = useState({
    title: clip.title || '',
    description: clip.description || '',
    privacy: 'public',
    uploadFormat: 'vertical' as 'vertical' | 'vertical_facecam' | 'original',
    autoCaptions: false
  });

  // Character limits for different platforms
  const getCharacterLimits = (platform: string) => {
    if (platform === 'youtube') {
      return { title: 100, description: 5000 };
    } else if (platform === 'tiktok') {
      return { title: 150, description: 2200 };
    }
    return { title: 100, description: 1000 };
  };
  const [showUploadModal, setShowUploadModal] = useState<string | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState<{
    isOpen: boolean;
    platform: string;
    videoUrl: string;
    videoId: string;
  }>({
    isOpen: false,
    platform: '',
    videoUrl: '',
    videoId: ''
  });

  const loadConnectedPlatforms = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/social/connected', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlatforms(data.platforms || {});
      } else if (response.status === 403) {
        // Subscription expired or not pro - this will be handled by the isPro check
        // console.log('Access denied to social media features - subscription may have expired');
      }
    } catch (error) {
      console.error('Error loading connected platforms:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadUploadHistory = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/social/uploads/${clip.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUploadHistory(data.uploads || []);
      }
    } catch (error) {
      console.error('Error loading upload history:', error);
    }
  }, [currentUser, clip.id]);

  useEffect(() => {
    if (currentUser) {
      // Always refresh subscription status when component mounts
      refreshSubscriptionStatus().then(() => {
        refetchPlanData();
      });
    }
  }, [currentUser, refreshSubscriptionStatus, refetchPlanData]);

  useEffect(() => {
    if (isPro && currentUser) {
      loadConnectedPlatforms();
      loadUploadHistory();
    }
  }, [isPro, currentUser, clip.id, loadConnectedPlatforms, loadUploadHistory]);

  const connectPlatform = async (platform: string) => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/social/auth/${platform}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Open OAuth popup
        const popup = window.open(
          data.auth_url,
          `Connect ${platform}`,
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        // Prevent duplicate handlers
        let messageHandled = false;
        
        // Listen for messages from the OAuth callback
        const handleMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin || messageHandled) return;
          
          if (event.data.type === 'oauth_success') {
            messageHandled = true; // Prevent duplicate handling
            // console.log('OAuth success received:', event.data);
            
            try {
              // Exchange the authorization code for access tokens
              const token = await currentUser.getIdToken();
              const exchangeResponse = await fetch(`/api/v1/social/auth/${platform}/exchange`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  code: event.data.code,
                  state: event.data.state
                })
              });

              if (exchangeResponse.ok) {
                // const result = await exchangeResponse.json();
                toast.success(`Successfully connected to ${platform}!`);
                // console.log('OAuth exchange successful:', result);
              } else {
                const errorText = await exchangeResponse.text();
                console.error('OAuth exchange failed with status:', exchangeResponse.status, errorText);
                toast.error(`Failed to complete ${platform} connection`);
              }
            } catch (error) {
              console.error('Error exchanging OAuth code:', error);
              toast.error(`Failed to complete ${platform} connection`);
            }
            
            // Clean up
            window.removeEventListener('message', handleMessage);
            
            // Reload platforms
            setTimeout(() => {
              loadConnectedPlatforms();
            }, 1000);
          } else if (event.data.type === 'oauth_error') {
            messageHandled = true;
            console.error('OAuth error received:', event.data.error);
            toast.error(`Failed to connect to ${platform}: ${event.data.error}`);
            
            // Clean up
            window.removeEventListener('message', handleMessage);
          }
        };

        // Add message listener
        window.addEventListener('message', handleMessage);

        // Fallback: Listen for popup close (in case message fails)
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            // Remove message listener if popup closed without message
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              // Only reload if we haven't already handled a message
              loadConnectedPlatforms();
            }, 1000);
          }
        }, 1000);
      } else if (response.status === 403) {
        toast.error('Pro subscription required. Please upgrade to connect social media accounts.');
      } else {
        toast.error(`Failed to initiate ${platform} connection`);
      }
    } catch (error) {
      console.error(`Error connecting to ${platform}:`, error);
      toast.error(`Error connecting to ${platform}`);
    }
  };

  const handleDisconnectPlatform = (platform: string) => {
    setShowDisconnectModal(platform);
  };

  const confirmDisconnectPlatform = async () => {
    const platform = showDisconnectModal;
    if (!currentUser || !platform) return;
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/social/disconnect/${platform}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const platformName = platform === 'youtube' ? 'YouTube' : 'TikTok';
      if (response.ok) {
        toast.success(`Successfully disconnected ${platformName}`);
        loadConnectedPlatforms();
      } else {
        toast.error(`Failed to disconnect ${platformName}`);
      }
    } catch (error) {
      console.error(`Error disconnecting ${platform}:`, error);
      const platformName = platform === 'youtube' ? 'YouTube' : 'TikTok';
      toast.error(`Error disconnecting ${platformName}`);
    } finally {
      setShowDisconnectModal(null);
    }
  };

  const copyVideoLink = async (videoUrl: string, platform: string) => {
    try {
      await navigator.clipboard.writeText(videoUrl);
      toast.success(`${platform === 'youtube' ? 'YouTube' : 'TikTok'} link copied to clipboard!`);
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Failed to copy link');
    }
  };

  const applyPlatformDefaults = (platform: string) => {
    const currentDescription = uploadForm.description || clip.description || '';
    
    if (platform === 'youtube') {
      // Add #Shorts hashtag if not present
      if (!currentDescription.toLowerCase().includes('#shorts')) {
        const newDescription = currentDescription.trim() 
          ? `${currentDescription}\n\n#Shorts` 
          : '#Shorts';
        setUploadForm(prev => ({ ...prev, description: newDescription }));
      }
    } else if (platform === 'tiktok') {
      // Add trending TikTok hashtags if description is short
      if (currentDescription.length < 50) {
        const tiktokHashtags = '#fyp #viral #trending';
        const newDescription = currentDescription.trim() 
          ? `${currentDescription}\n\n${tiktokHashtags}` 
          : tiktokHashtags;
        setUploadForm(prev => ({ ...prev, description: newDescription }));
      }
    }
  };

  const uploadToPlatform = async (platform: string) => {
    if (!currentUser) return;
    
    try {
      setUploading(prev => ({ ...prev, [platform]: true }));
      setUploadProgress(prev => ({ ...prev, [platform]: { step: 'Initializing upload...', progress: 10 } }));
      
      const token = await currentUser.getIdToken();
      
      // For YouTube, we need to ensure we have the selected format export first
      if (platform === 'youtube') {
        const selectedFormat = uploadForm.uploadFormat;
        setUploadProgress(prev => ({ ...prev, [platform]: { step: `Checking ${selectedFormat} format...`, progress: 20 } }));
        
        let hasExport = false;
        
        if (selectedFormat === 'original') {
          // Original format always exists (it's the clip itself)
          hasExport = true;
        } else {
          hasExport = !!(clip.export_formats && clip.export_formats[selectedFormat]);
        }
        
        if (!hasExport && selectedFormat !== 'original') {
          // Auto-generate export in selected format
          const formatNames = {
            'vertical': 'vertical format',
            'vertical_facecam': 'vertical facecam format',
            'original': 'original format'
          };
          
          setUploadProgress(prev => ({ ...prev, [platform]: { step: `Creating ${formatNames[selectedFormat]}...`, progress: 30 } }));
          
          try {
            const exportResponse = await fetch('/api/v1/export', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                clip_id: clip.id,
                format: selectedFormat,
                auto_captions: uploadForm.autoCaptions
              })
            });
            
            if (exportResponse.ok) {
              const exportData = await exportResponse.json();
              // Update clip data with new export
              clip.export_formats = { ...clip.export_formats, [selectedFormat]: exportData.file_url };
              setUploadProgress(prev => ({ ...prev, [platform]: { step: `${formatNames[selectedFormat]} ready!`, progress: 60 } }));
            } else {
              throw new Error(`Failed to create ${selectedFormat} export`);
            }
          } catch (exportError) {
            console.error('Error creating export:', exportError);
            toast.error(`Failed to create ${formatNames[selectedFormat]}`);
            setUploading(prev => ({ ...prev, [platform]: false }));
            setUploadProgress(prev => ({ ...prev, [platform]: { step: 'Export failed', progress: 0 } }));
            return;
          }
        } else {
          const formatNames = {
            'vertical': 'Vertical format found!',
            'vertical_facecam': 'Vertical facecam format found!',
            'original': 'Original format ready!'
          };
          setUploadProgress(prev => ({ ...prev, [platform]: { step: formatNames[selectedFormat], progress: 60 } }));
        }
      }
      
      const uploadTarget = platform === 'youtube' 
        ? (uploadForm.uploadFormat === 'original' ? 'YouTube' : 'YouTube Shorts')
        : 'TikTok';
      setUploadProgress(prev => ({ ...prev, [platform]: { step: `Uploading to ${uploadTarget}...`, progress: 80 } }));
      
      const response = await fetch(`/api/v1/social/upload/${clip.id}/${platform}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(uploadForm)
      });

      if (response.ok) {
        const data = await response.json();
        setUploadProgress(prev => ({ ...prev, [platform]: { step: 'Upload complete!', progress: 100 } }));
        setShowUploadModal(null);
        
        // Reload upload history immediately for live updates
        await loadUploadHistory();
        
        // Show simple success toast
        const successTarget = platform === 'youtube' 
          ? (uploadForm.uploadFormat === 'original' ? 'YouTube' : 'YouTube Shorts')
          : 'TikTok';
        toast.success(`Successfully uploaded to ${successTarget}!`);
        
        // Show success modal with view video option
        if (data.video_url) {
          setShowSuccessModal({
            isOpen: true,
            platform: successTarget,
            videoUrl: data.video_url,
            videoId: data.video_id || ''
          });
        }
      } else {
        const errorData = await response.json();
        let errorMessage = errorData.detail || `Failed to upload to ${platform}`;
        
        // Provide better instructions for auth errors
        if (errorMessage.includes('refresh token') || errorMessage.includes('reconnect')) {
          errorMessage = `${platform === 'youtube' ? 'YouTube' : 'TikTok'} connection expired. Please disconnect and reconnect your account in the settings above.`;
        }
        
        setUploadProgress(prev => ({ ...prev, [platform]: { step: 'Upload failed', progress: 0 } }));
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error(`Error uploading to ${platform}:`, error);
      setUploadProgress(prev => ({ ...prev, [platform]: { step: 'Upload failed', progress: 0 } }));
      toast.error(`Error uploading to ${platform}`);
    } finally {
      setUploading(prev => ({ ...prev, [platform]: false }));
      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [platform]: { step: '', progress: 0 } }));
      }, 2000);
    }
  };

  if (!currentUser || isGuest) {
    return (
      <div className={`bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-dashed border-gray-300 opacity-60 ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-300 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Upload className="w-8 h-8 text-white opacity-70" />
          </div>
          <h3 className="text-xl font-bold text-gray-500 mb-2">Auto-Upload to Social Media</h3>
          <p className="text-gray-500 mb-2">Login required to upload clips to social platforms</p>
          <button
            disabled
            className="bg-gray-300 text-white px-6 py-3 rounded-xl font-semibold cursor-not-allowed"
          >
            Sign in to enable
          </button>
        </div>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className={`bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-dashed border-gray-300 ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
            <Upload className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Auto-Upload to Social Media</h3>
          <p className="text-gray-600 mb-4">
            Automatically upload your clips to YouTube Shorts and TikTok with Pro
          </p>
          <button
            onClick={() => window.location.href = '/dashboard#pricing'}
            className="bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 transition-colors"
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`bg-white rounded-xl p-6 shadow-sm border border-gray-200 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Upload className="w-5 h-5 mr-2" />
              Social Media Upload
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Connect your accounts and upload clips directly to social platforms
            </p>
          </div>
        </div>

        {/* Platform Connections */}
        <div className="space-y-4 mb-6">
          {/* YouTube */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">YouTube Shorts</h4>
                {platforms.youtube?.connected ? (
                  <p className="text-sm text-gray-600">
                    Connected to {platforms.youtube.channel_title}
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">Not connected</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {platforms.youtube?.connected ? (
                <>
                  <button
                    onClick={() => setShowUploadModal('youtube')}
                    disabled={uploading.youtube}
                    className="btn-primary text-sm flex items-center min-w-[100px] justify-center"
                  >
                    {uploading.youtube ? (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center mb-1">
                          <Clock className="w-4 h-4 mr-1 animate-spin" />
                          <span className="text-xs">
                            {uploadProgress.youtube?.step ? uploadProgress.youtube.step.substring(0, 20) + '...' : 'Uploading...'}
                          </span>
                        </div>
                        {uploadProgress.youtube?.progress && (
                          <div className="w-full bg-white bg-opacity-30 rounded-lg h-1">
                            <div 
                              className={`bg-white h-1 transition-all duration-300 ${((showUploadModal && uploadProgress[showUploadModal]?.progress) || 0) >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                              style={{ width: `${uploadProgress.youtube.progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleDisconnectPlatform('youtube')}
                    className="btn-secondary text-sm flex items-center"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => connectPlatform('youtube')}
                  className="btn-primary text-sm"
                >
                  Connect
                </button>
              )}
            </div>
          </div>

          {/* TikTok */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">TikTok</h4>
                {platforms.tiktok?.connected ? (
                  <p className="text-sm text-gray-600">
                    Connected to @{platforms.tiktok.username}
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">Not connected</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {platforms.tiktok?.connected ? (
                <>
                  <button
                    onClick={() => setShowUploadModal('tiktok')}
                    disabled={uploading.tiktok}
                    className="btn-primary text-sm flex items-center"
                  >
                    {uploading.tiktok ? (
                      <>
                        <Clock className="w-4 h-4 mr-1 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleDisconnectPlatform('tiktok')}
                    className="btn-secondary text-sm flex items-center"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Disconnect
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <button
                    onClick={() => connectPlatform('tiktok')}
                    className="btn-secondary text-sm opacity-60 cursor-not-allowed"
                    disabled
                  >
                    Coming Soon
                  </button>
                  {/* <p className="text-xs text-gray-500 mt-1">TikTok API approval pending</p> */}
                </div>
              )}
            </div>
          </div>

          {/* Instagram Reels */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Instagram Reels</h4>
                <p className="text-sm text-gray-600">Not connected</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-center">
                <button
                  className="btn-secondary text-sm opacity-60 cursor-not-allowed"
                  disabled
                >
                  Coming Soon
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Upload History */}
        {uploadHistory.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Upload History</h4>
            <div className="space-y-2">
              {uploadHistory.map((upload) => (
                <div 
                  key={upload.id} 
                  onClick={() => window.open(upload.video_url, '_blank')}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      upload.platform === 'youtube' ? 'bg-red-600' : 'bg-black'
                    }`}>
                      {upload.platform === 'youtube' ? (
                        <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white">
                          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {upload.metadata.title || 'Untitled'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Uploaded {(() => {
                          // Handle various timestamp formats and ensure proper timezone conversion
                          let date;
                          
                          if (typeof upload.uploaded_at === 'object' && upload.uploaded_at !== null && 'toDate' in upload.uploaded_at) {
                            // Firestore Timestamp object
                            date = (upload.uploaded_at as any).toDate();
                          } else if (typeof upload.uploaded_at === 'string') {
                            // Handle string timestamps - ensure proper UTC interpretation
                            let dateString = upload.uploaded_at;
                            
                            // If it's an ISO string without timezone info, assume it's UTC
                            if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
                              dateString = dateString + 'Z';
                            }
                            
                            date = new Date(dateString);
                          } else {
                            // Direct Date object or fallback
                            date = new Date(upload.uploaded_at);
                          }
                          
                          // Validate the date
                          if (isNaN(date.getTime())) {
                            return 'Unknown time';
                          }
                          
                          const localDate = date.toLocaleDateString(undefined, { 
                            year: 'numeric', 
                            month: 'numeric', 
                            day: 'numeric'
                          });
                          const localTime = date.toLocaleTimeString(undefined, { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true
                          });
                          return `${localDate} at ${localTime}`;
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyVideoLink(upload.video_url, upload.platform);
                      }}
                      className="text-gray-400 hover:text-black transition-colors p-1 rounded"
                      title="Copy link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <div className="text-gray-400 group-hover:text-gray-600 transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Disconnect Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!showDisconnectModal}
        onClose={() => setShowDisconnectModal(null)}
        onConfirm={confirmDisconnectPlatform}
        title={`Disconnect ${showDisconnectModal === 'youtube' ? 'YouTube' : 'TikTok'}?`}
        message={`Are you sure you want to disconnect your ${showDisconnectModal === 'youtube' ? 'YouTube' : 'TikTok'} account? You will need to reconnect to upload clips to this platform.`}
        confirmText="Disconnect"
        cancelText="Cancel"
        type="danger"
        icon={<Unlink size={24} />}
      />

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  Upload to {showUploadModal === 'youtube' ? 'YouTube Shorts' : 'TikTok'}
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => applyPlatformDefaults(showUploadModal!)}
                    className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-lg hover:bg-red-200 transition-colors"
                    title="Apply platform-specific defaults"
                  >
                    Auto-optimize
                  </button>
                  <button
                    onClick={() => setShowUploadModal(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Title
                    </label>
                    <span className={`text-xs ${
                      uploadForm.title.length > getCharacterLimits(showUploadModal!).title 
                        ? 'text-red-500' 
                        : uploadForm.title.length > getCharacterLimits(showUploadModal!).title * 0.8 
                          ? 'text-red-500' 
                          : 'text-gray-500'
                    }`}>
                      {uploadForm.title.length}/{getCharacterLimits(showUploadModal!).title}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      uploadForm.title.length > getCharacterLimits(showUploadModal!).title 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter video title"
                    maxLength={getCharacterLimits(showUploadModal!).title}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <span className={`text-xs ${
                      uploadForm.description.length > getCharacterLimits(showUploadModal!).description 
                        ? 'text-red-500' 
                        : uploadForm.description.length > getCharacterLimits(showUploadModal!).description * 0.8 
                          ? 'text-red-500' 
                          : 'text-gray-500'
                    }`}>
                      {uploadForm.description.length}/{getCharacterLimits(showUploadModal!).description}
                    </span>
                  </div>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      uploadForm.description.length > getCharacterLimits(showUploadModal!).description 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    rows={3}
                    placeholder="Enter video description"
                    maxLength={getCharacterLimits(showUploadModal!).description}
                  />
                </div>

                {showUploadModal === 'youtube' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Privacy
                      </label>
                      <select
                        value={uploadForm.privacy}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, privacy: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      >
                        <option value="public">Public</option>
                        <option value="unlisted">Unlisted</option>
                        <option value="private">Private</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload Format
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="uploadFormat"
                            value="vertical"
                            checked={uploadForm.uploadFormat === 'vertical'}
                            onChange={(e) => setUploadForm(prev => ({ ...prev, uploadFormat: e.target.value as any }))}
                            className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">📱 Vertical Short</div>
                            <div className="text-xs text-gray-600">Standard 9:16 format for YouTube Shorts</div>
                          </div>
                        </label>
                        
                        <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="uploadFormat"
                            value="vertical_facecam"
                            checked={uploadForm.uploadFormat === 'vertical_facecam'}
                            onChange={(e) => setUploadForm(prev => ({ ...prev, uploadFormat: e.target.value as any }))}
                            className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-900">🎯 Facecam Vertical</span>
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Experimental
                              </span>
                            </div>
                            <div className="text-xs text-gray-600">Smart facecam separation with vertical layout</div>
                          </div>
                        </label>
                        
                        <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="uploadFormat"
                            value="original"
                            checked={uploadForm.uploadFormat === 'original'}
                            onChange={(e) => setUploadForm(prev => ({ ...prev, uploadFormat: e.target.value as any }))}
                            className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">📺 Original Clip</div>
                            <div className="text-xs text-gray-600">Upload as regular YouTube video (not Short)</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Auto Captions Pill */}
                    <div className="flex justify-center">
                      <button
                        onClick={() => {
                          if (!isPro) {
                            navigate('/pricing');
                            return;
                          }
                          setUploadForm(prev => ({ ...prev, autoCaptions: !prev.autoCaptions }));
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-all duration-200 ${
                          uploadForm.autoCaptions && isPro
                            ? 'bg-red-100 text-red-700 border border-red-200'
                            : isPro
                            ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                            : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-pointer hover:bg-red-50 hover:border-red-200'
                        }`}
                      >
                        {!isPro && <Crown className="w-4 h-4 text-red-600" />}
                        <span>Add Auto Captions</span>
                        {!isPro && <span className="text-xs text-red-600 font-semibold ml-1">Pro</span>}
                      </button>
                    </div>

                    {uploadForm.autoCaptions && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-gray-700">
                          AI-generated captions will be added to your video. Perfect for accessibility and engagement!
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setShowUploadModal(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => uploadToPlatform(showUploadModal)}
                  disabled={uploading[showUploadModal] || !uploadForm.title.trim()}
                  className="btn-primary flex items-center relative overflow-hidden"
                >
                  {uploading[showUploadModal] ? (
                    <>
                      <div className="flex flex-col items-center">
                        <div className="flex items-center mb-1">
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          <span className="text-sm">
                            {uploadProgress[showUploadModal]?.step || 'Uploading...'}
                          </span>
                        </div>
                        <div className="w-full bg-white bg-opacity-30 rounded-lg h-1">
                          <div 
                            className={`bg-white h-1 transition-all duration-300 ${((showUploadModal && uploadProgress[showUploadModal]?.progress) || 0) >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                            style={{ width: `${uploadProgress[showUploadModal]?.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-gray-500" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Upload Successful! 🎉
              </h3>
              
              <p className="text-gray-600 mb-6">
                Your clip has been successfully uploaded to {showSuccessModal.platform}
              </p>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowSuccessModal({ isOpen: false, platform: '', videoUrl: '', videoId: '' })}
                  className="btn-secondary"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    window.open(showSuccessModal.videoUrl, '_blank');
                    setShowSuccessModal({ isOpen: false, platform: '', videoUrl: '', videoId: '' });
                  }}
                  className="btn-primary flex items-center"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Video
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaUploader; 